import Head from "next/head";
import Link from "next/link";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { Header } from "@/components/LandingPage/Header";
import { Footer } from "@/components/LandingPage/Footer";
import { Container } from "@/components/LandingPage/Container";
import logger from "@/utils/logger";

// Helper function to parse JSON safely
const parseJSON = (text: string) => {
  try {
    return JSON.parse(text);
  } catch (error) {
    return null;
  }
};

// Deep diff function to find differences between two objects
const deepDiff = (obj1: any, obj2: any, path: string = ""): any[] => {
  const differences: any[] = [];

  // Handle null/undefined cases
  if (obj1 === null || obj1 === undefined || obj2 === null || obj2 === undefined) {
    if (obj1 !== obj2) {
      differences.push({
        path,
        type: "changed",
        oldValue: obj1,
        newValue: obj2,
      });
    }
    return differences;
  }

  // Handle primitive types
  if (typeof obj1 !== "object" || typeof obj2 !== "object") {
    if (obj1 !== obj2) {
      differences.push({
        path,
        type: "changed",
        oldValue: obj1,
        newValue: obj2,
      });
    }
    return differences;
  }

  // Handle arrays
  if (Array.isArray(obj1) || Array.isArray(obj2)) {
    if (!Array.isArray(obj1) || !Array.isArray(obj2)) {
      differences.push({
        path,
        type: "changed",
        oldValue: obj1,
        newValue: obj2,
      });
      return differences;
    }
    
    // Compare array elements
    const maxLength = Math.max(obj1.length, obj2.length);
    for (let i = 0; i < maxLength; i++) {
      const newPath = `${path}[${i}]`;
      
      if (i >= obj1.length) {
        // Item added in obj2
        differences.push({
          path: newPath,
          type: "added",
          oldValue: undefined,
          newValue: obj2[i],
        });
      } else if (i >= obj2.length) {
        // Item removed from obj1
        differences.push({
          path: newPath,
          type: "removed",
          oldValue: obj1[i],
          newValue: undefined,
        });
      } else {
        // Compare items at same index
        differences.push(...deepDiff(obj1[i], obj2[i], newPath));
      }
    }
    return differences;
  }

  // Handle objects
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  const allKeys = new Set([...keys1, ...keys2]);

  allKeys.forEach((key) => {
    const newPath = path ? `${path}.${key}` : key;
    
    if (!(key in obj1)) {
      differences.push({
        path: newPath,
        type: "added",
        oldValue: undefined,
        newValue: obj2[key],
      });
    } else if (!(key in obj2)) {
      differences.push({
        path: newPath,
        type: "removed",
        oldValue: obj1[key],
        newValue: undefined,
      });
    } else {
      differences.push(...deepDiff(obj1[key], obj2[key], newPath));
    }
  });

  return differences;
};

// Component to render JSON with highlighted differences
const JsonWithDiff = ({ json, differences, side }: { json: any; differences: any[]; side: "left" | "right" }) => {
  const renderValue = (value: any, path: string = "", indent: number = 0): JSX.Element => {
    const diff = differences.find((d) => d.path === path);
    let className = "";
    
    if (diff) {
      if (diff.type === "removed" && side === "left") {
        className = "bg-red-900/30 text-white font-medium";
      } else if (diff.type === "added" && side === "right") {
        className = "bg-green-900/30 text-white font-medium";
      } else if (diff.type === "changed") {
        className = side === "left" ? "bg-red-900/30 text-white font-medium" : "bg-green-900/30 text-white font-medium";
      }
    }

    const indentStr = "  ".repeat(indent);

    if (value === null) return <span className={className}>{indentStr}null</span>;
    if (value === undefined) return <span className={className}>{indentStr}undefined</span>;
    
    if (typeof value !== "object") {
      return (
        <span className={className}>
          {indentStr}{typeof value === "string" ? `"${value}"` : String(value)}
        </span>
      );
    }

    if (Array.isArray(value)) {
      // For arrays, we need to check if there are any removed items we should show
      const maxIndex = Math.max(
        value.length,
        ...differences
          .filter(d => d.path.startsWith(path + "[") && d.type === "removed")
          .map(d => {
            const match = d.path.match(/\[(\d+)\]/);
            return match ? parseInt(match[1]) + 1 : 0;
          })
      );

      return (
        <>
          <span>{indentStr}[</span>
          {Array.from({ length: maxIndex }).map((_, index) => {
            const itemPath = `${path}[${index}]`;
            const diff = differences.find(d => d.path === itemPath);
            
            if (diff && diff.type === "removed" && side === "left") {
              // Show removed item on left side
              return (
                <div key={index} className="bg-red-900/30 text-white font-medium rounded px-1 my-1">
                  {renderValue(diff.oldValue, itemPath, indent + 1)}
                  {index < maxIndex - 1 && ","}
                </div>
              );
            } else if (diff && diff.type === "added" && side === "right") {
              // Show added item on right side
              return (
                <div key={index} className="bg-green-900/30 text-white font-medium rounded px-1 my-1">
                  {renderValue(diff.newValue, itemPath, indent + 1)}
                  {index < value.length - 1 && ","}
                </div>
              );
            } else if (index < value.length) {
              // Show existing item
              return (
                <div key={index}>
                  {renderValue(value[index], itemPath, indent + 1)}
                  {index < value.length - 1 && ","}
                </div>
              );
            }
            
            return null;
          })}
          <span>{indentStr}]</span>
        </>
      );
    }

    const entries = Object.entries(value);
    return (
      <>
        <span>{indentStr}{"{"}</span>
        {entries.map(([key, val], index) => {
          const newPath = path ? `${path}.${key}` : key;
          const keyDiff = differences.find((d) => d.path === newPath);
          let keyClassName = "";
          
          if (keyDiff) {
            if (keyDiff.type === "removed" && side === "left") {
              keyClassName = "bg-red-900/20 rounded px-1";
            } else if (keyDiff.type === "added" && side === "right") {
              keyClassName = "bg-green-900/20 rounded px-1";
            }
          }

          return (
            <div key={key} className={keyClassName}>
              <span className="text-purple">{"  ".repeat(indent + 1)}&quot;{key}&quot;</span>: {renderValue(val, newPath, 0)}
              {index < entries.length - 1 && ","}
            </div>
          );
        })}
        <span>{indentStr}{"}"}</span>
      </>
    );
  };

  return <pre className="font-mono text-sm text-gray-100">{renderValue(json)}</pre>;
};

export default function JsonDiffPage() {
  const [jsonInput1, setJsonInput1] = useState("");
  const [jsonInput2, setJsonInput2] = useState("");
  const [differences, setDifferences] = useState<any[]>([]);
  const [error, setError] = useState("");
  const [showDiff, setShowDiff] = useState(false);

  const compareJson = () => {
    try {
      if (!jsonInput1.trim() || !jsonInput2.trim()) {
        setError("Please enter JSON in both fields to compare");
        setShowDiff(false);
        return;
      }

      const parsed1 = parseJSON(jsonInput1);
      const parsed2 = parseJSON(jsonInput2);

      if (!parsed1) {
        setError("Invalid JSON format in the first field. Please check your syntax.");
        setShowDiff(false);
        return;
      }

      if (!parsed2) {
        setError("Invalid JSON format in the second field. Please check your syntax.");
        setShowDiff(false);
        return;
      }

      const diffs = deepDiff(parsed1, parsed2);
      setDifferences(diffs);
      setError("");
      setShowDiff(true);

      // Log the diff action
      logger.log(
        "prettify",
        "JSON Compared",
        `User compared two JSON objects (${jsonInput1.length} vs ${jsonInput2.length} characters)`
      );
    } catch (err) {
      setError("An error occurred while comparing JSON. Please check your inputs.");
      setShowDiff(false);
    }
  };

  const clearAll = () => {
    setJsonInput1("");
    setJsonInput2("");
    setDifferences([]);
    setError("");
    setShowDiff(false);
  };



  return (
    <div className="bg-[#1E1E1E] min-h-screen">
      <Header dark={false} />

      <main className="pt-32 pb-20">
        <Container className="max-w-7xl">
          <div className="flex flex-col items-center justify-center gap-2">
            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-6xl">
              JSON Diff Checker
            </h1>

            <p className="text-sm text-gray-400">
              Compare two JSON objects and highlight differences. No data is stored on our servers.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8 mt-8">
            {/* First JSON Input */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white">Original JSON</h2>
              </div>
              <textarea
                value={jsonInput1}
                onChange={(e) => setJsonInput1(e.target.value)}
                placeholder="Paste your original JSON here..."
                className="w-full h-80 p-4 bg-gray-800 border border-gray-600 rounded-lg text-gray-100 font-mono text-sm resize-none focus:ring-2 focus:ring-purple focus:border-transparent"
                aria-label="Original JSON input"
              />
            </div>

            {/* Second JSON Input */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white">Modified JSON</h2>
              </div>
              <textarea
                value={jsonInput2}
                onChange={(e) => setJsonInput2(e.target.value)}
                placeholder="Paste your modified JSON here..."
                className="w-full h-80 p-4 bg-gray-800 border border-gray-600 rounded-lg text-gray-100 font-mono text-sm resize-none focus:ring-2 focus:ring-purple focus:border-transparent"
                aria-label="Modified JSON input"
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-8 p-4 bg-red-900/20 border border-red-500/50 rounded-lg">
              <p className="text-red-300 font-medium">Error: {error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-4 justify-center mb-8">
            <button
              onClick={compareJson}
              disabled={!jsonInput1.trim() || !jsonInput2.trim()}
              className={`px-6 py-3 font-semibold rounded-lg transition-colors focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 ${
                jsonInput1.trim() && jsonInput2.trim()
                  ? "bg-purple hover:bg-purple-600 text-white focus:ring-purple"
                  : "bg-gray-600 text-gray-400 cursor-not-allowed"
              }`}
            >
              Compare JSON
            </button>
            <button
              onClick={clearAll}
              className="px-6 py-3 border border-gray-600 hover:border-gray-500 text-gray-300 hover:text-white font-semibold rounded-lg transition-colors focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:ring-offset-gray-900"
            >
              Clear All
            </button>

          </div>

          {/* Diff Results */}
          {showDiff && (
            <div className="space-y-8 mb-12">
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white mb-2">
                  {differences.length === 0 ? "No differences found" : `Found ${differences.length} difference${differences.length === 1 ? "" : "s"}`}
                </h3>
                {differences.length > 0 && (
                  <div className="flex items-center justify-center gap-6 text-sm text-gray-400">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-red-900/30 border border-red-500/50 rounded"></div>
                      <span>Removed/Changed (Original)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-green-900/30 border border-green-500/50 rounded"></div>
                      <span>Added/Changed (Modified)</span>
                    </div>
                  </div>
                )}
              </div>

              {differences.length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-white">Original JSON with Highlights</h4>
                    <div className="p-4 bg-gray-900 border border-gray-600 rounded-lg overflow-x-auto">
                      <JsonWithDiff 
                        json={parseJSON(jsonInput1)} 
                        differences={differences}
                        side="left"
                      />
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-white">Modified JSON with Highlights</h4>
                    <div className="p-4 bg-gray-900 border border-gray-600 rounded-lg overflow-x-auto">
                      <JsonWithDiff 
                        json={parseJSON(jsonInput2)} 
                        differences={differences}
                        side="right"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Call to Action - Store JSON */}
          {showDiff && (
            <div className="bg-gradient-to-r from-purple/10 to-purple/5 border border-purple/20 rounded-xl p-8 mb-12 text-center">
              <div className="mb-4">
                <svg
                  className="w-12 h-12 text-purple mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
                  />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">
                Track JSON Changes Over Time
              </h3>
              <p className="text-gray-300 text-lg mb-6 max-w-2xl mx-auto">
                Store your JSON with automatic versioning. Track changes, restore previous versions, and collaborate with your team using myJson.online&apos;s powerful API.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Link
                  href="/signup?utm_source=json-diff"
                  className="px-8 py-4 bg-purple hover:bg-purple-600 text-white font-bold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-purple/25 inline-flex items-center gap-2"
                >
                  Get started for free
                </Link>
              </div>
              <div className="mt-4 flex items-center justify-center gap-6 text-sm text-gray-400">
                <div className="flex items-center gap-1">
                  <svg
                    className="w-4 h-4 text-green-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Version Control
                </div>
                <div className="flex items-center gap-1">
                  <svg
                    className="w-4 h-4 text-green-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Restore Versions
                </div>
              </div>
            </div>
          )}
        </Container>
      </main>

      <Footer />
    </div>
  );
}